// Trap pieces (mines): self-arming, trigger on qualifying enemies (§23.2).
// Slice simplification (documented): mines build themselves over buildTime instead of
// requiring worker placement, then arm 2 s later.

import { buildingDef, cardDef, trapDef, unitDef } from '../../content/registry';
import { TUNING } from '../../content/tuning';
import { dist } from '../../shared/vec';
import type { Sim } from '../sim';
import type { Squad } from '../state';

const dt = TUNING.dt;

export function stepTraps(sim: Sim): void {
  const st = sim.state;

  for (const m of [...st.mines.values()]) {
    const td = trapDef(m.defId);
    if (!m.built) {
      m.buildProgress += dt / td.buildTime;
      if (m.buildProgress >= 1) m.built = true;
      continue;
    }
    if (m.armDelay > 0) {
      m.armDelay -= dt;
      continue;
    }
    const behavior = td.behavior ?? 'mine';

    if (behavior === 'rootRing') {
      // Circle of Thorns: ≥minSquads qualifying squads inside → root them all
      const inside: Squad[] = [];
      for (const s of st.squads.values()) {
        if (!sim.areHostile(s.owner, m.owner)) continue;
        if (dist(s.pos, m.pos) > td.triggerRadius) continue;
        inside.push(s);
      }
      const qualifying = inside.filter((s) => td.triggerSizeTags.includes(unitDef(s.defId).sizeTag));
      if (qualifying.length >= (td.minSquads ?? 2)) {
        m.triggered = true;
        m.dead = true;
        st.mines.delete(m.id);
        sim.pushEvent({ type: 'TrapTriggered', pos: { ...m.pos } });
        for (const s of inside) {
          const d = unitDef(s.defId);
          if (td.triggerSizeTags.includes(d.sizeTag)) {
            for (const eff of td.effects) {
              if (eff.fx === 'root') s.rootedRemaining = Math.max(s.rootedRemaining, eff.duration);
              else if (eff.fx === 'damage') sim.damageSquad(s, eff.amount, { isRanged: false, attackerOwner: m.owner });
              else if (eff.fx === 'healReduction') {
                s.healingTakenMult = Math.min(s.healingTakenMult, eff.mult);
                s.healingTakenRemaining = Math.max(s.healingTakenRemaining, eff.duration);
              }
            }
          } else if (d.sizeTag === 'large') {
            s.buffs.push({ stat: 'moveSpeed', mult: 0.7, remaining: 3 });
          }
        }
      }
      continue;
    }

    if (behavior === 'spellSeal') {
      // passive: consumed by the spell-cast hook in cards.ts
      continue;
    }

    if (behavior === 'gravityWell') {
      // Gravity Well (AD-20): triggers on a Large/Giant or ≥minSquads; pulls smalls
      // to the center, slows bigs, and cancels movement bursts (bursts = the pull)
      let trigger = false;
      let squadsIn = 0;
      for (const s of st.squads.values()) {
        if (!sim.areHostile(s.owner, m.owner)) continue;
        if (dist(s.pos, m.pos) > td.triggerRadius) continue;
        const d = unitDef(s.defId);
        if (d.sizeTag === 'large' || d.sizeTag === 'giant') trigger = true;
        squadsIn++;
      }
      if (trigger || squadsIn >= (td.minSquads ?? 2)) {
        m.triggered = true;
        m.dead = true;
        st.mines.delete(m.id);
        sim.pushEvent({ type: 'TrapTriggered', pos: { ...m.pos } });
        for (const s of [...st.squads.values()]) {
          if (!sim.areHostile(s.owner, m.owner)) continue;
          if (dist(s.pos, m.pos) > td.triggerRadius + 2) continue;
          const d = unitDef(s.defId);
          if (d.sizeTag === 'small' || d.sizeTag === 'medium') {
            sim.teleportSquad(s, { ...m.pos }, 6); // yanked toward the singularity
            s.rootedRemaining = Math.max(s.rootedRemaining, 1);
          } else {
            s.buffs.push({ stat: 'moveSpeed', mult: 0.65, remaining: 5 });
          }
          for (const eff of td.effects) {
            if (eff.fx === 'damage') sim.damageSquad(s, eff.amount, { isRanged: false, attackerOwner: m.owner });
          }
        }
      }
      continue;
    }

    if (behavior === 'seedGrove') {
      // Seed of the Sleeping Grove: erupts into a temporary defensive structure
      let trigger = false;
      let squadsIn = 0;
      for (const s of st.squads.values()) {
        if (!sim.areHostile(s.owner, m.owner)) continue;
        if (dist(s.pos, m.pos) > td.triggerRadius) continue;
        const d = unitDef(s.defId);
        if (d.sizeTag === 'large' || d.sizeTag === 'giant' || s.isBoss) trigger = true;
        squadsIn++;
      }
      if (trigger || squadsIn >= (td.minSquads ?? 3)) {
        m.triggered = true;
        m.dead = true;
        st.mines.delete(m.id);
        sim.pushEvent({ type: 'TrapTriggered', pos: { ...m.pos } });
        if (td.eruptBuildingId) {
          sim.spawnBuilding(td.eruptBuildingId, m.owner, m.pos, {
            complete: true,
            expiresSeconds: td.eruptSeconds ?? 25,
            cardId: m.cardId,
          });
        }
      }
      continue;
    }

    // contact mines (Aegis Minefield)
    for (const s of st.squads.values()) {
      if (!sim.areHostile(s.owner, m.owner)) continue;
      const d = unitDef(s.defId);
      if (!td.triggerSizeTags.includes(d.sizeTag)) continue; // Small units do not trigger
      if (dist(s.pos, m.pos) > td.triggerRadius) continue;
      // detonate
      m.triggered = true;
      m.dead = true;
      st.mines.delete(m.id);
      sim.pushEvent({ type: 'TrapTriggered', pos: { ...m.pos } });
      for (const eff of td.effects) {
        if (eff.fx === 'damage') {
          sim.damageSquad(s, eff.amount, { isRanged: false, attackerOwner: m.owner });
        } else if (eff.fx === 'slow') {
          // Giants: 20% instead of full slow (bible IC-20)
          const mag = d.sizeTag === 'giant' ? 0.2 : eff.magnitude;
          s.buffs.push({ stat: 'moveSpeed', mult: 1 - mag, remaining: eff.duration });
        } else if (eff.fx === 'mark' && d.sizeTag === 'giant') {
          s.marks.push({ markId: eff.markId, remaining: eff.duration });
        }
      }
      break;
    }
  }

  // gates (Oathbound Gate): mark the first N enemy squads that come within reach
  for (const b of st.buildings.values()) {
    if (b.state !== 'active' || b.gateMarksRemaining <= 0) continue;
    const gate = buildingDef(b.defId).gate;
    if (!gate) continue;
    for (const s of st.squads.values()) {
      if (b.gateMarksRemaining <= 0) break;
      if (!sim.areHostile(s.owner, b.owner)) continue;
      if (dist(s.pos, b.pos) > gate.radius) continue;
      const markId = `gate-mark-${b.owner}`;
      if (s.marks.some((m) => m.markId === markId && m.remaining > 0)) continue;
      s.marks.push({ markId, remaining: gate.markDuration });
      b.gateMarksRemaining--;
      sim.pushEvent({ type: 'TrapTriggered', pos: { ...s.pos } });
    }
  }

  // active-copy accounting: a field counts while ≥1 of its mines lives
  for (const p of st.players) {
    const fieldsByCard = new Map<string, Set<number>>();
    for (const m of st.mines.values()) {
      if (m.owner !== p.id || m.cardId === null) continue;
      let set = fieldsByCard.get(m.cardId);
      if (!set) {
        set = new Set();
        fieldsByCard.set(m.cardId, set);
      }
      set.add(m.fieldId);
    }
    for (const cid of Object.keys(p.cards)) {
      if (cardDef(cid).category !== 'trap') continue;
      p.cards[cid].activeCopies = fieldsByCard.get(cid)?.size ?? 0;
    }
  }
}
